import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import CameraTile from 'src/features/camera/components/CameraTile';
import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import { MonitoringGateway as MonitoringGatewayType } from 'src/features/monitoring/types';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { eventTypeTranslationMap } from 'src/features/systems/utils/constants';
import { useGetVisitor } from 'src/features/visitor/api/useGetVisitor';
import { GatewayAction, GatewayActionState } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import {
    GeneralBlock,
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { Translation } from 'src/types';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

type Props = {
    gateway: MonitoringGatewayType;
    isOpen: boolean;
    visitors?: {
        currentLicensePlate?: string;
        pastVisitorId?: string;
    };
    isBusy?: boolean;
    setGatewayAction: (v: GatewayActionState) => void;
    index: number;
};
export default function SingleMonitoringGateway({ gateway, isOpen, isBusy, visitors, setGatewayAction, index }: Props) {
    const ref = React.useRef<HTMLDivElement>(null);
    const { parkingId } = useParking();

    const accessGatewaysActions = useHasPermissions([PERMISSION.ACCESS_GATEWAYS_MONITORING_ACTIONS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const { formatMessage, locale } = useIntl();
    const { data } = useGetVisitor({
        parkingId,
        visitorId: visitors?.pastVisitorId || '',
        config: { enabled: !!visitors?.pastVisitorId },
    });

    const { translation, entryZone, exitZone } = React.useMemo(() => {
        const translation = translate(gateway.translations, locale);
        const entryZone = gateway.entry_zone ? translate(gateway.entry_zone.translations, locale)?.name : '-';
        const exitZone = gateway.exit_zone ? translate(gateway.exit_zone.translations, locale)?.name : '-';

        return {
            translation,
            entryZone,
            exitZone,
        };
    }, [gateway, locale]);

    React.useEffect(() => {
        if (!ref.current) return;

        if (isOpen) ref.current.style.height = ref.current.scrollHeight + 'px';
        else ref.current.style.height = '0px';
    }, [isOpen]);

    return (
        <div className="flex flex-col">
            <GeneralBlock className="flex-1 rounded-b-none border-b-0">
                <div className="flex w-full flex-row items-center justify-between">
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle className="text-md text-gray-900">{translation?.name}</GeneralBlockTitle>
                            <GeneralBlockSubtitle className="text-sm">
                                {translation?.description || '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <Badge theme={isBusy ? 'red' : 'green'}>
                        {formatMessage(isBusy ? parkingStatusMessages.busy : parkingStatusMessages.free)}
                    </Badge>
                </div>
                {/*TODO: Change this to actual camera*/}
                <CameraTile
                    index={index}
                    title={translation?.name || ''}
                    preview="http://192.168.1.98:8002/media/photos/1769588599889596.jpg"
                    key={0}
                />
                <div
                    className={cn('flex flex-col gap-4 overflow-hidden duration-500 ease-in-out', !isOpen && '-mt-4')}
                    ref={ref}
                >
                    <div className="flex w-full justify-center gap-5 text-center text-xs">
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.totalPassesToday)}
                            value={gateway.total_passes_today}
                        />
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.failedPassesToday)}
                            value={gateway.failed_passes_today}
                        />
                    </div>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.entryZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{entryZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.exitZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{exitZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.currentVisitor} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {visitors?.currentLicensePlate ?? '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.lastVisitor} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {data ? (
                                    <>
                                        <LinkWithIcon
                                            to={`/${PATH.VISITORS}/visitors/${data.id}`}
                                            linkTitle={data?.main_identifier?.value ?? ''}
                                            hasPermissions={accessVisitors}
                                        />
                                        <Badge theme={visitorTypeBadgeOptions[data.type].theme}>
                                            <FontAwesomeIcon
                                                icon={visitorTypeBadgeOptions[data.type].icon || ICON_MAP.CLIENT}
                                            />
                                            {formatMessage(visitorTypeBadgeOptions[data.type].label)}
                                        </Badge>
                                    </>
                                ) : (
                                    '-'
                                )}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.requiredIdentifiers} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.required_identifier_types
                                    .map((type) => formatMessage(identifierTypeTranslationMap[type]))
                                    .join(', ')}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.currentOperation} />
                            </GeneralBlockTitle>
                            {/*TODO: Change when we have the operation*/}
                            <GeneralBlockSubtitle>-</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.message} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.message ? formatMessage(eventTypeTranslationMap[gateway.message]) : '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                </div>
            </GeneralBlock>

            {accessGatewaysActions && (
                <GeneralBlock className="flex w-full flex-row overflow-scroll rounded-t-none">
                    <Button
                        className="flex-1"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.OPEN,
                            })
                        }
                    >
                        <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                        <FormattedMessage {...parkingStatusMessages.raise} />
                    </Button>
                    <Button
                        className="flex-1"
                        variant="destructiveLight"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.EMERGENCY,
                            })
                        }
                    >
                        <FontAwesomeIcon icon={ICON_MAP.DANGER} />
                        <FormattedMessage {...parkingStatusMessages.emergency} />
                    </Button>
                    <Button
                        className="flex-1"
                        variant="outline"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.LOCK,
                            })
                        }
                    >
                        <FontAwesomeIcon icon={ICON_MAP.PASSWORD} />
                        <FormattedMessage {...parkingStatusMessages.lock} />
                    </Button>
                </GeneralBlock>
            )}
        </div>
    );
}
